import { useCurrentUserCompany } from './api'
import { useIsInternalUser } from './useIsInternalUser'

/**
 * Is the reader currently inside a company whose own `kind` is `'demo'`?
 *
 * VRG-836. The one caller that matters is `ExampleKindBadge`, and the question it
 * answers there is "does this badge discriminate anything". A demo company is
 * provisioned entirely by Vera (`ProvisionDemoUseCase` stamps `example_kind = 'demo'`
 * on every seeded row), so inside one the badge is on 100% of rows and carries no
 * information at all. After `TakeOverDemoUseCase` flips `kind` to `'standard'` the
 * seeded rows KEEP their `example_kind` — never billed, grandfathered in beside the
 * brand's own records — and the same badge starts doing real work. So the badge's
 * usefulness turns on the COMPANY's kind, not the row's, which is the only reason this
 * hook exists.
 *
 * ## Why `useCurrentUserCompany` and not a prop
 *
 * The badge is rendered from eight call sites, three of them inside shared table cells
 * (`ProductCell`, `CertificateCell`, `UserBlock`) that have no company in scope and are
 * reused by every list in the app. Threading a prop through all of them is eight chances
 * to forget — the same argument the badge's own `!kind` guard records for living in the
 * component rather than at its call sites.
 *
 * `useCurrentUserCompany` is a react-query read on a single cache key with a 5-minute
 * `staleTime` and no refetch-on-mount, so the N badge instances in a table share one
 * request and one cache entry. The cost is N query observers on a value that effectively
 * never changes, which is the deliberate trade against N prop threads that can drift.
 *
 * ## Why internal staff are gated out
 *
 * `GET /companies/admin/me` resolves the caller's OWN company, so for Vera staff
 * operating AS staff there is nothing for it to resolve and the request is wasted.
 * Returning `false` for them is also the correct answer: staff looking at another
 * company's rows across an `/internal/*` surface SHOULD see which ones are seeded.
 *
 * `useIsInternalUser` is already false inside a company's SPACE (VERA-876), so a staffer
 * who has entered a demo's space reads that demo company here and the badges correctly
 * disappear — every row they are looking at is Vera's.
 */
export const useIsDemoCompany = (): boolean => {
  const isInternal = useIsInternalUser()
  const { data: company } = useCurrentUserCompany({ enabled: !isInternal })
  return company?.kind === 'demo'
}
